import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Box,
  Alert,
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import FirmwareVersionPicker from './FirmwareVersionPicker';

const PHASE_LABELS = {
  starting: 'Starting update...',
  uploading: 'Uploading firmware...',
  verifying: 'Verifying integrity...',
  committing: 'Committing update...',
  rebooting: 'Device is rebooting...',
};

export default function FirmwareUpdateSection({ currentFirmware, onFlash, disabled }) {
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [flashing, setFlashing] = useState(false);
  const [phase, setPhase] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleProgress = useCallback((ph, received, total) => {
    setPhase(ph);
    setProgress(total > 0 ? Math.round((received / total) * 100) : 0);
  }, []);

  const handleUpdate = useCallback(async () => {
    if (!selectedVersion || !onFlash) return;
    setFlashing(true);
    setError(null);
    setSuccess(false);
    setPhase('starting');
    setProgress(0);

    try {
      await onFlash(selectedVersion, handleProgress);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Firmware update failed');
    } finally {
      setFlashing(false);
      setPhase(null);
    }
  }, [selectedVersion, onFlash, handleProgress]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Firmware Update
        </Typography>

        {currentFirmware && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Current firmware: <strong>{currentFirmware}</strong>
          </Typography>
        )}

        <Box sx={{ mb: 2 }}>
          <FirmwareVersionPicker
            model="PTQS1005"
            onVersionSelect={setSelectedVersion}
          />
        </Box>

        <Button
          variant="contained"
          startIcon={<SystemUpdateAltIcon />}
          onClick={handleUpdate}
          disabled={disabled || flashing || !selectedVersion}
        >
          {flashing ? 'Updating...' : 'Update Firmware'}
        </Button>

        {flashing && phase && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {PHASE_LABELS[phase] || phase}
            </Typography>
            <LinearProgress
              variant={phase === 'uploading' ? 'determinate' : 'indeterminate'}
              value={progress}
            />
            {phase === 'uploading' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {progress}%
              </Typography>
            )}
          </Box>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Firmware updated successfully! Device is rebooting.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

FirmwareUpdateSection.propTypes = {
  currentFirmware: PropTypes.string,
  onFlash: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
